import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import type { ToastShowOptions } from '../context/ToastContext'
import { copyImageToClipboard } from '../lib/copyImageToClipboard'
import { copyTextToClipboard } from '../lib/copyText'
import type { ReferenceImageUrlHints } from '../lib/referenceImageUrl'
import { resolveAbsoluteHttpImageUrl } from '../lib/referenceImageIngestUrl'
import { resolveMediaImage } from '../lib/resolvedMediaImage'
import { ChromeToolbarButton } from './ChromeActions'
import { chromeIconButtonClass } from './ChromeSurface'
import { ThemedBottomSheet } from './modal/ThemedBottomSheet'

export type ImageContextMenuItem = {
  id: string
  label: string
  run: () => void | Promise<void>
}

type ToastFn = (opts: ToastShowOptions) => void

export function buildImageContextMenuItems(args: {
  /** 与当前 img / 灯箱展示的 URL 一致（所见即所用） */
  imageUrl: string
  onSave: () => Promise<void>
  showToast: ToastFn
  hints?: ReferenceImageUrlHints
}): ImageContextMenuItem[] {
  const viewerUrl = args.imageUrl.trim()
  if (!viewerUrl) return []
  const media = resolveMediaImage(viewerUrl, args.hints)
  // 展示用 displayUrl（DEV 下是 /uploads 相对路径走 Vite 反代），
  // 但"复制链接 / 新标签页打开"必须用绝对 URL（别人也要能打开）
  const absoluteUrl = resolveAbsoluteHttpImageUrl(media.raw, args.hints)

  return [
    {
      id: 'save',
      label: '保存图片',
      run: async () => {
        try {
          await args.onSave()
        } catch (e) {
          args.showToast({
            message: e instanceof Error ? e.message : '保存失败',
            variant: 'error',
          })
        }
      },
    },
    {
      id: 'copy-image',
      label: '复制图片',
      run: async () => {
        try {
          await copyImageToClipboard(viewerUrl, args.hints)
          args.showToast({ message: '已复制图片', variant: 'success', autoCloseMs: 2800 })
        } catch (e) {
          args.showToast({
            message: e instanceof Error ? e.message : '复制图片失败',
            variant: 'error',
          })
        }
      },
    },
    {
      id: 'copy',
      label: '复制图片链接',
      run: async () => {
        try {
          await copyTextToClipboard(absoluteUrl)
          args.showToast({ message: '已复制链接', variant: 'success', autoCloseMs: 2800 })
        } catch (e) {
          args.showToast({
            message: e instanceof Error ? e.message : '复制失败',
            variant: 'error',
          })
        }
      },
    },
    {
      id: 'open',
      label: '在新标签页打开',
      run: () => {
        window.open(absoluteUrl, '_blank', 'noopener,noreferrer')
      },
    },
  ]
}

type DesktopMenuProps = {
  angular: boolean
  open: boolean
  anchor: { x: number; y: number }
  items: ImageContextMenuItem[]
  onClose: () => void
  /** 需高于 ImageLightbox（z-200） */
  zClass?: string
}

/**
 * 键鼠环境：固定在指针附近的右键菜单。
 */
export function ImageContextDesktopMenu({
  angular,
  open,
  anchor,
  items,
  onClose,
  zClass = 'z-[240]',
}: DesktopMenuProps) {
  const panelRef = useRef<HTMLDivElement | null>(null)
  const [shift, setShift] = useState({ x: 0, y: 0 })

  useLayoutEffect(() => {
    if (!open || !panelRef.current) {
      setShift({ x: 0, y: 0 })
      return
    }
    const el = panelRef.current
    const r = el.getBoundingClientRect()
    const pad = 8
    let dx = 0
    let dy = 0
    if (r.right > window.innerWidth - pad) dx = window.innerWidth - pad - r.right
    if (r.bottom > window.innerHeight - pad) dy = window.innerHeight - pad - r.bottom
    if (r.left + dx < pad) dx = pad - r.left
    if (r.top + dy < pad) dy = pad - r.top
    setShift({ x: dx, y: dy })
  }, [open, anchor.x, anchor.y, items.length])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    const onPointer = (e: MouseEvent | PointerEvent) => {
      const t = e.target
      if (t instanceof Node && panelRef.current?.contains(t)) return
      onClose()
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onPointer, true)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onPointer, true)
    }
  }, [open, onClose])

  if (typeof document === 'undefined' || !open || items.length === 0) return null

  const round = angular ? 'rounded-none' : 'rounded-lg'
  const itemRound = angular ? 'rounded-none' : 'rounded-md'

  return createPortal(
    <>
      <div className="fixed inset-0 z-[239] bg-transparent" aria-hidden onClick={onClose} />
      <div
        ref={panelRef}
        role="menu"
        className={[
          'fixed min-w-[11.5rem] border border-line bg-panel py-1 text-sm text-ink shadow-card',
          round,
          zClass,
        ].join(' ')}
        style={{
          left: anchor.x + shift.x,
          top: anchor.y + shift.y,
        }}
      >
        {items.map((it) => (
          <button
            key={it.id}
            type="button"
            role="menuitem"
            className={[
              'flex w-full px-3 py-2.5 text-left text-[13px] font-medium transition hover:bg-surface-muted',
              itemRound,
            ].join(' ')}
            onClick={() => {
              void Promise.resolve(it.run()).finally(onClose)
            }}
          >
            {it.label}
          </button>
        ))}
      </div>
    </>,
    document.body,
  )
}

type MobileSheetProps = {
  angular: boolean
  open: boolean
  title?: string
  items: ImageContextMenuItem[]
  onClose: () => void
  zClass?: string
}

/**
 * 触控 / 微信内：底部操作表，不替代系统长按图片菜单。
 */
export function ImageContextMobileSheet({
  angular,
  open,
  title = '图片操作',
  items,
  onClose,
  zClass = 'z-[240]',
}: MobileSheetProps) {
  if (items.length === 0) return null

  return (
    <ThemedBottomSheet open={open} onClose={onClose} title={title} angular={angular} zClass={zClass}>
      <p className="typo-lib-section mb-2 px-1 text-center text-ink">{title}</p>
      <div className="scroll-region flex max-h-[min(52dvh,22rem)] min-h-0 flex-col gap-1.5 overflow-y-auto overscroll-y-contain">
        {items.map((it) => (
          <ChromeToolbarButton
            key={it.id}
            type="button"
            className={[
              'w-full !justify-start text-left !py-3 !text-sm !font-medium',
              angular ? '!rounded-none' : '!rounded-xl',
            ].join(' ')}
            onClick={() => void Promise.resolve(it.run()).finally(onClose)}
          >
            {it.label}
          </ChromeToolbarButton>
        ))}
      </div>
      <ChromeToolbarButton
        type="button"
        className={['mt-2 w-full !py-2.5 !font-semibold', angular ? '!rounded-none' : '!rounded-xl'].join(' ')}
        onClick={onClose}
      >
        取消
      </ChromeToolbarButton>
    </ThemedBottomSheet>
  )
}

/** 右上角「更多」触发器（触控 / 微信内用） */
export function ImageMoreActionsTrigger({
  angular,
  label = '更多图片操作',
  onOpen,
}: {
  angular: boolean
  label?: string
  onOpen: () => void
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={(e) => {
        e.stopPropagation()
        onOpen()
      }}
      className={[chromeIconButtonClass(angular), 'pointer-events-auto z-[7] !h-8 !w-8 !min-h-8 !min-w-8'].join(' ')}
    >
      <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden>
        <circle cx="12" cy="5" r="1.85" />
        <circle cx="12" cy="12" r="1.85" />
        <circle cx="12" cy="19" r="1.85" />
      </svg>
    </button>
  )
}

export function useImageContextPayload() {
  const [desktop, setDesktop] = useState<{ x: number; y: number; src: string; stem: string } | null>(
    null,
  )
  const [sheet, setSheet] = useState<{ src: string; stem: string } | null>(null)

  const openDesktop = useCallback((e: React.MouseEvent, src: string, stem: string) => {
    setSheet(null)
    setDesktop({ x: e.clientX, y: e.clientY, src, stem })
  }, [])

  const openSheet = useCallback((src: string, stem: string) => {
    setDesktop(null)
    setSheet({ src, stem })
  }, [])

  const closeAll = useCallback(() => {
    setDesktop(null)
    setSheet(null)
  }, [])

  return { desktop, sheet, openDesktop, openSheet, closeAll }
}
